const Policy = (props: { print?: boolean }) => {
  return (
    <div className="container mx-auto px-4">
      <div className="flex flex-wrap mt-4 mb-12">
        <div className="w-full mb-12 px-4">
          <h2 className="text-4xl font-semibold">Privacy Policy</h2>
        </div>
        <div className="w-full mb-12 px-4">
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            Effective Date: 04/09/2025
          </p>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            DuckFlow provides a Chrome extension
            Plugin that enables users to capture data
            from LinkedIn and other permitted sources and import it directly
            into your connected Customer Relationship Management (CRM) system.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">
            Information We Collect
          </h3>
          <ul className="list-disc list-inside text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            <li>
              <strong>User Information:</strong> Name, email address, and
              authentication details needed to connect the Extension with your
              CRM.
            </li>
            <li>
              <strong>Authentication Tokens:</strong> Secure tokens generated
              from our platform (<code>app.duckflow.io</code>) to verify your
              identity, authenticate your session, and perform authorized
              requests on your behalf.
            </li>
            <li>
              <strong>Captured Data:</strong> Contact details, professional
              information, or other publicly available data from LinkedIn or
              other supported sources, as directed by you.
            </li>
            <li>
              <strong>CRM Data:</strong> Information imported into your CRM,
              limited to the scope you authorize.
            </li>
            <li>
              <strong>Usage Data:</strong> Technical details such as browser
              type, extension version, device information, and interaction logs
              for troubleshooting and performance monitoring.
            </li>
          </ul>

          <h3 className="text-2xl font-semibold mt-8 mb-4">
            How We Use Your Information
          </h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            We use collected information to:
          </p>
          <ul className="list-disc list-inside text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            <li>Provide and operate the Extension.</li>
            <li>
              Authenticate your account and perform authorized requests via{" "}
              <code>app.duckflow.io</code>.
            </li>
            <li>Facilitate the transfer of selected data into your CRM.</li>
            <li>Improve and optimize the Extension’s functionality.</li>
            <li>Offer technical support and resolve issues.</li>
            <li>Ensure compliance with applicable laws and our Terms of Use.</li>
          </ul>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            We do <strong>not</strong> sell, rent, or trade your personal data.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">Data Sharing</h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            We may share your information only in the following cases:
          </p>
          <ul className="list-disc list-inside text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            <li>
              <strong>With Your CRM:</strong> To transfer data you choose to
              capture.
            </li>
            <li>
              <strong>With Service Providers:</strong> Trusted third parties who
              assist in operating and maintaining the Extension (e.g., hosting,
              analytics, support).
            </li>
            <li>
              <strong>For Legal Reasons:</strong> If required by law,
              regulation, or legal process, or to protect our rights and
              security.
            </li>
          </ul>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            Authentication tokens are only used to securely connect your
            Extension session with <code>app.duckflow.io</code> and are never
            shared with third parties.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">Data Security</h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            We implement industry-standard technical, administrative, and
            organizational measures to protect your information against
            unauthorized access, alteration, disclosure, or destruction.
            Authentication tokens are securely stored and transmitted using
            encryption. However, no method of transmission over the internet or
            method of storage is 100% secure.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">Data Retention</h3>
          <ul className="list-disc list-inside text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            <li>
              Authentication tokens are retained only for the duration necessary
              to maintain your active session and perform authorized requests.
            </li>
            <li>
              Captured data is transferred directly to your CRM and is not
              permanently stored by us, except as necessary for technical
              processing.
            </li>
            <li>
              User and technical data (such as logs) are retained only as long
              as needed to provide the service and fulfill legal obligations.
            </li>
          </ul>

          <h3 className="text-2xl font-semibold mt-8 mb-4">Your Rights</h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            Depending on your location, you may have the following rights under
            applicable data protection laws (e.g., GDPR, CCPA):
          </p>
          <ul className="list-disc list-inside text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            <li>Access, correction, or deletion of your personal data.</li>
            <li>Restriction or objection to processing.</li>
            <li>Data portability.</li>
            <li>Withdrawal of consent (where applicable).</li>
          </ul>

          <h3 className="text-2xl font-semibold mt-8 mb-4">
            Third-Party Platforms
          </h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            The Extension interacts with LinkedIn and other external sources.
            Please note:
          </p>
          <ul className="list-disc list-inside text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            <li>
              We do not control how third-party platforms collect, use, or
              process your data.
            </li>
            <li>
              You are responsible for complying with the terms of service and
              privacy policies of those platforms.
            </li>
          </ul>

          <h3 className="text-2xl font-semibold mt-8 mb-4">
            Children’s Privacy
          </h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            The Extension is not intended for use by individuals under 16 years
            of age (or the applicable age of majority in your jurisdiction). We
            do not knowingly collect personal information from children.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">Changes</h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            We may update this Privacy Policy from time to time. Any changes
            will be posted with an updated “Effective Date.”
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h3>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            For more information about our privacy practices, if you have
            questions, or if you would like to make a complaint, please contact
            us at:
          </p>
          <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-500">
            DuckFlow <br />
            Email: <a href="mailto:privacy@example.com">privacy@duckflow.io</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Policy;
